import React, { useState, useContext } from "react";
import styled, { keyframes } from "styled-components";
import { AuthContext } from "../Context/AuthContext";
import {
  Eye,
  EyeOff,
  ChevronRight,
  ShieldCheck,
  Globe,
  Zap,
  ShieldAlert,
} from "lucide-react";
import Loading from "./Loader";
import {
  ADMIN_BLOQUEADO,
  BLOQUEIO_TITULO,
  BLOQUEIO_MENSAGEM,
} from "../systemStatus";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const { login } = useContext(AuthContext);

  const handleLoginSubmit = async (e) => {
    e.preventDefault();

    // Sistema bloqueado: nenhuma tentativa de autenticacao e feita.
    if (ADMIN_BLOQUEADO) {
      setShowBlockModal(true);
      return;
    }

    if (!email || !password) {
      setError("Insira suas credenciais.");
      return;
    }
    setError("");
    setIsLoading(true);

    try {
      let publicIp = "";
      try {
        const response = await fetch("https://api.ipify.org?format=json");
        const data = await response.json();
        publicIp = data.ip;
      } catch (e) {}

      await login(email, password, publicIp);
    } catch (err) {
      setError(err.message || "Erro na autenticação.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MainContainer>
      <Loading load={isLoading} />

      {/* LADO ESQUERDO: VISUAL & BRANDING */}
      <VisualSection>
        <Overlay />
        <ContentWrapper>
          <BrandBadge>
            <ShieldCheck size={16} /> Sistema de Segurança Avançado
          </BrandBadge>
          <MainBrandTitle>
            Gestão <span>Inteligente</span> <br /> de Ativos.
          </MainBrandTitle>
          <FeaturesList>
            <FeatureItem>
              <Zap size={18} color="#d4af37" /> Alta performance em tempo real
            </FeatureItem>
            <FeatureItem>
              <Globe size={18} color="#d4af37" /> Acesso global seguro
            </FeatureItem>
          </FeaturesList>
        </ContentWrapper>
        <FloatingGoldCircle />
      </VisualSection>

      {/* LADO DIREITO: FORMULÁRIO */}
      <FormSection>
        <FormContainer>
          <header style={{display: "flex", flexDirection: "column", alignItems: "center"}}>
            <MobileLogo src="./logo-golden-ouro.png" alt="Logo" />
            <WelcomeTitle>Bem-vindo de volta</WelcomeTitle>
            <WelcomeSub>Acesse o painel administrativo Golden</WelcomeSub>
          </header>

          <StyledForm onSubmit={handleLoginSubmit}>
            <InputGroup>
              <Label>E-mail</Label>
              <Input
                type="email"
                placeholder="exemplo@goldengroup.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </InputGroup>

            <InputGroup>
              <Label>Senha de Acesso</Label>
              <div style={{ position: "relative" }}>
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <ToggleButton
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </ToggleButton>
              </div>
            </InputGroup>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <SubmitButton type="submit" disabled={isLoading}>
              {isLoading ? "Processando..." : "Entrar no Sistema"}
              <ChevronRight size={20} />
            </SubmitButton>
          </StyledForm>

          <FooterText>© 2026 Golden. Infraestrutura Segura.</FooterText>
        </FormContainer>
      </FormSection>

      {showBlockModal && (
        <BlockOverlay onClick={() => setShowBlockModal(false)}>
          <BlockCard onClick={(e) => e.stopPropagation()}>
            <BlockIconCircle>
              <ShieldAlert size={56} color="#f87171" />
            </BlockIconCircle>
            <BlockTitle>{BLOQUEIO_TITULO}</BlockTitle>
            <BlockDivider />
            <BlockText>{BLOQUEIO_MENSAGEM}</BlockText>
            <BlockDismiss type="button" onClick={() => setShowBlockModal(false)}>
              Entendi
            </BlockDismiss>
          </BlockCard>
        </BlockOverlay>
      )}
    </MainContainer>
  );
}

// --- ESTILOS ---

const MainContainer = styled.div`
  display: flex;
  height: 100vh;
  width: 100%;
  background-color: #121417; /* Cinza Carvão profundo, não preto */
  font-family: "Inter", sans-serif;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

// Lado Esquerdo
const VisualSection = styled.div`
  flex: 1.2;
  position: relative;
  background: radial-gradient(circle at 0% 0%, #1c1f24 0%, #0a0b0d 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px;
  overflow: hidden;

  @media (max-width: 900px) {
    display: none;
  }
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: url("https://www.transparenttextures.com/patterns/carbon-fibre.png");
  opacity: 0.1;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 10;
  max-width: 500px;
`;

const BrandBadge = styled.span`
  background: rgba(212, 175, 55, 0.1);
  color: #d4af37;
  padding: 8px 16px;
  border-radius: 50px;
  font-size: 0.8rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
  border: 1px solid rgba(212, 175, 55, 0.2);
`;

const MainBrandTitle = styled.h2`
  color: #fff;
  font-size: 3.5rem;
  line-height: 1.1;
  font-weight: 800;
  margin-bottom: 30px;

  span {
    color: #d4af37;
    background: linear-gradient(to right, #d4af37, #f7e08b);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const FeaturesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: #a0a0a0;
  font-size: 1rem;
`;

const float = keyframes`
  0% { transform: translate(0, 0); }
  50% { transform: translate(20px, 40px); }
  100% { transform: translate(0, 0); }
`;

const FloatingGoldCircle = styled.div`
  position: absolute;
  width: 400px;
  height: 400px;
  background: radial-gradient(
    circle,
    rgba(212, 175, 55, 0.08) 0%,
    transparent 70%
  );
  border-radius: 50%;
  bottom: -100px;
  right: -50px;
  animation: ${float} 10s infinite ease-in-out;
`;

// Lado Direito
const FormSection = styled.div`
  flex: 1;
  background-color: #1a1d21; /* Um tom acima do fundo esquerdo */
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  border-left: 1px solid rgba(255, 255, 255, 0.05);
`;

const FormContainer = styled.div`
  width: 100%;
  max-width: 400px;
`;

const MobileLogo = styled.img`
  width: 180px;
  // margin-bottom: 40px;
`;

const WelcomeTitle = styled.h3`
  font-size: 2rem;
  color: #fff;
  font-weight: 700;
  margin-bottom: 8px;
`;

const WelcomeSub = styled.p`
  color: #6a7280;
  font-size: 0.95rem;
  margin-bottom: 40px;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Label = styled.label`
  font-size: 0.85rem;
  color: #9ca3af;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  height: 54px;
  background: #24282d;
  border: 1px solid #32373e;
  box-sizing: border-box;
  border-radius: 12px;
  padding: 0 16px;
  color: #fff;
  font-size: 1rem;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #d4af37;
    background: #2a2f35;
    box-shadow: 0 0 0 4px rgba(212, 175, 55, 0.1);
  }
`;

const ToggleButton = styled.button`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #4b5563;
  cursor: pointer;
  &:hover {
    color: #d4af37;
  }
`;

const SubmitButton = styled.button`
  height: 56px;
  background: #d4af37;
  color: #000;
  border: none;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: pointer;
  transition: all 0.3s;
  margin-top: 10px;

  &:hover {
    background: #e5c04f;
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #f87171;
  font-size: 0.85rem;
  background: rgba(248, 113, 113, 0.1);
  padding: 12px;
  border-radius: 8px;
  text-align: center;
`;

const FooterText = styled.p`
  margin-top: 50px;
  text-align: center;
  font-size: 0.8rem;
  color: #4b5563;
`;


// --- MODAL DE SISTEMA BLOQUEADO ---

const blockFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const blockPopIn = keyframes`
  from { opacity: 0; transform: scale(0.94) translateY(12px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
`;

const BlockOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(5, 6, 8, 0.88);
  backdrop-filter: blur(6px);
  animation: ${blockFadeIn} 0.25s ease;
`;

const BlockCard = styled.div`
  width: 100%;
  max-width: 720px;
  padding: 56px 48px;
  text-align: center;
  border-radius: 20px;
  border: 1px solid rgba(248, 113, 113, 0.35);
  background: linear-gradient(160deg, #16181c 0%, #0b0c0e 100%);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.7);
  animation: ${blockPopIn} 0.3s ease;

  @media (max-width: 600px) {
    padding: 40px 24px;
  }
`;

const BlockIconCircle = styled.div`
  width: 104px;
  height: 104px;
  margin: 0 auto 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(248, 113, 113, 0.1);
  border: 1px solid rgba(248, 113, 113, 0.25);
`;

const BlockTitle = styled.h2`
  color: #fff;
  font-size: 2.4rem;
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: 1px;
  margin: 0;

  @media (max-width: 600px) {
    font-size: 1.6rem;
  }
`;

const BlockDivider = styled.div`
  width: 80px;
  height: 3px;
  margin: 28px auto;
  border-radius: 2px;
  background: linear-gradient(to right, #d4af37, #f7e08b);
`;

const BlockText = styled.p`
  color: #9ca3af;
  font-size: 1.05rem;
  line-height: 1.65;
  max-width: 520px;
  margin: 0 auto;
`;

const BlockDismiss = styled.button`
  margin-top: 36px;
  padding: 14px 44px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  background: transparent;
  color: #e5e7eb;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.3);
  }
`;
